/* istanbul ignore file */

/**
 * This is a Bullet App convention file.
 * DON'T RENAME OR MOVE TO OTHER FOLDERS.
 *
 * Please only export a default method e.g. called `getRoutes`,
 * which returns an array of `Route` (from react-router-dom v4.x)
 */

import React, {useContext} from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Loadable from 'react-loadable';
import {matchPath} from 'react-router';
import {Redirect, Route, Switch} from 'react-router-dom';

import PrivateRoute from '@stubhub/react-private-route';
import ReloadPage from '@stubhub/react-reload-page';
import Spinner from '@stubhub/react-spinner';

import {GlobalRegistryContext} from '../context';
import FEATURE_FLAGS from '../utils/featureFlags';
import {getLangPath} from '../utils/utility';

import DismissLogin from './myaccount/Dismiss';
import Layout from './myaccount/layout';

const TRUE_REG = /^(1|true)$/i;

const REACT_APP_ENABLE_NONE_UI_LOGIN = TRUE_REG.test(process.env.REACT_APP_ENABLE_NONE_UI_LOGIN);

const AuthRoute = (props) => (!REACT_APP_ENABLE_NONE_UI_LOGIN ? <PrivateRoute {...props} /> : <Route {...props} />);

const Settings = Loadable({
  loader: () => import(/* webpackChunkName: "settings" */ './myaccount/settings'),
  loading: Spinner,
});

const Profile = Loadable({
  loader: () => import(/* webpackChunkName: "profile" */ './myaccount/profile'),
  loading: Spinner,
});

const Orders = Loadable({
  loader: () => import(/* webpackChunkName: "orders" */ './myaccount/orders'),
  loading: Spinner,
});

const OrderDetails = Loadable({
  loader: () => import(/* webpackChunkName: "tickets" */ './myaccount/ordersDetails'),
  loading: Spinner,
});

const Rewards = Loadable({
  loader: () => import(/* webpackChunkName: "rewards" */ './myaccount/rewards'),
  loading: Spinner,
});

const Guest = Loadable({
  loader: () => import(/* webpackChunkName: "tickets" */ './myaccount/orders/guest'),
  loading: Spinner,
});

const Favorites = Loadable({
  loader: () => import(/* webpackChunkName: "favorites" */ './myaccount/favorites'),
  loading: Spinner,
});

const ProListings = Loadable({
  loader: () => import(/* webpackChunkName: "proListings" */ './myaccount/proListings'),
  loading: Spinner,
});

const EditListing = Loadable({
  loader: () => import(/* webpackChunkName: "edit-listing" */ './myaccount/listings/edit'),
  loading: Spinner,
});

const Sales = Loadable({
  loader: () => import(/* webpackChunkName: "sales" */ './myaccount/sales'),
  loading: Spinner,
});

const TicketUpload = Loadable({
  loader: () => import(/* webpackChunkName: "ticket-upload" */ './myaccount/sales/ticket-upload'),
  loading: Spinner,
});

const ViewSoldPDF = Loadable({
  loader: () => import(/* webpackChunkName: "view-sold-pdf" */ './myaccount/sales/view-sold-pdf'),
  loading: Spinner,
});

const Print = Loadable({
  loader: () => import(/* webpackChunkName: "sales-print" */ './myaccount/sales/print'),
  loading: Spinner,
});

const Payments = Loadable({
  loader: () => import(/* webpackChunkName: "payments" */ './myaccount/payments'),
  loading: Spinner,
});

const PaymentsSellerConfig = Loadable({
  loader: () => import(/* webpackChunkName: "payments" */ './myaccount/payments/config/seller'),
  loading: Spinner,
});

const PaymentsBuyerConfig = Loadable({
  loader: () => import(/* webpackChunkName: "payments" */ './myaccount/payments/config/buyer'),
  loading: Spinner,
});

const Relist = Loadable({
  loader: () => import(/* webpackChunkName: "relist" */ './myaccount/relist'),
  loading: Spinner,
});

const SellTools = Loadable({
  loader: () => import(/* webpackChunkName: "sellTools" */ './myaccount/sellTools'),
  loading: Spinner,
});

// import {ORDER_STATUS, LISTING_STATUS, SALE_STATUS, PAYMENT_STATUS} from './myaccount/constant';

const shouldHideSideLayout = (location) => {
  const ticketPage = /my\/order\/\d*\/tickets/;
  const vendorPage = /my\/order\/\d*\/vendor/;
  const relistPage = /my\/order\/relist/;
  const sellToolsPage = /my\/sellTools/;
  const printPage = /print/;
  const {pathname} = location;

  return [ticketPage, vendorPage, printPage, relistPage, sellToolsPage].some((path) => {
    return path.exec(pathname);
  });
};

const MainLayout = (props) => {
  const {match, location} = props;

  const hideSideLayout = shouldHideSideLayout(location);
  const globalRegistry = useContext(GlobalRegistryContext);
  const showSpeedyProfile = globalRegistry.getFeatureValue(FEATURE_FLAGS.mya.profile);
  const showSpeedyFavorites = globalRegistry.getFeatureValue(FEATURE_FLAGS.mya.favorites);
  const showSpeedySales = globalRegistry.getFeatureValue(FEATURE_FLAGS.mya.sales);
  const showSpeedyOrders = globalRegistry.getFeatureValue(FEATURE_FLAGS.mya.orders);
  const showSpeedyPayments = globalRegistry.getFeatureValue(FEATURE_FLAGS.mya.payments);

  const fullScreenRoutes = [
    `${match.path}/order/:saleId/tickets`,
    `${match.path}/order/relist`,
    `${match.path}/sellTools`,
  ];
  const useFullScreenLayout = fullScreenRoutes.some((route) => matchPath(location.pathname, route));

  return (
    <>
      <Layout
        hideSideLayout={hideSideLayout}
        className={cn({hideSide: hideSideLayout}, {fullScreen: useFullScreenLayout})}
      >
        <Switch>
          {showSpeedyOrders && [
            <Route path={`${match.path}/orders`} component={Orders} key="orders" />,
            <Route path={`${match.path}/order/:saleId/tickets`} component={OrderDetails} key="tickets" />,
            <Route path={`${match.path}/order/relist`} component={Relist} key="relist" />,
          ]}
          <Route path={`${match.path}/sales/large`} component={ReloadPage} key="spa" />
          {showSpeedySales && [
            <Route path={`${match.path}/sale/viewPDF`} component={ViewSoldPDF} key="ViewSoldPDF-sale" />,
            <Route path={`${match.path}/sale/uploadPDF`} component={TicketUpload} key="TicketUpload-sale" />,
            <Route path={`${match.path}/sales`} component={Sales} key="Sales" />,
          ]}
          <Route path={`${match.path}/listings/large`} component={ReloadPage} key="spa" />
          <Route path={`${match.path}/listings`} component={ProListings} key="Listings" exact />,
          <Route path={`${match.path}/listings/edit`} component={EditListing} key="EditListing" />,
          <Route path={`${match.path}/listing/viewPDF`} component={ViewSoldPDF} key="ViewSoldPDF-listing" />,
          <Route path={`${match.path}/listing/uploadPDF`} component={TicketUpload} key="TicketUpload-listing" />,
          <Route
            /* <Route path={`${match.path}/shipping/print`} render={(props) => <Print {...props} isShippingToLMS={false}/>} key="Sales" /> */
            path={`${match.path}/lms/print`}
            render={(props) => <Print {...props} isShippingToLMS={true} />}
            key="Sales"
          />
          ,
          <Route path={`${match.path}/settings`} component={Settings} key="settings" />
          <Route path={`${match.path}/rewards`} component={Rewards} key="rewards" />
          {showSpeedyProfile && <Route path={`${match.path}/profile`} component={Profile} key="profile" />}
          {showSpeedyFavorites && <Route path={`${match.path}/favorites`} component={Favorites} key="favorites" />}
          {showSpeedyPayments && [
            <Route
              path={`${match.path}/payments/config/seller`}
              component={PaymentsSellerConfig}
              key="paymentsSellerConfig"
            />,
            <Route
              path={`${match.path}/payments/config/buyer`}
              component={PaymentsBuyerConfig}
              key="paymentsBuyerConfig"
            />,
            <Route path={`${match.path}/payments`} component={Payments} key="payments" />,
          ]}
          <Route path={`${match.path}/sellTools`} exact={true} component={SellTools} key="sellTools" />
          <Redirect from={`${match.path}`} exact={true} to={`${match.path}/profile`} />
          {/* <Redirect from={`${match.path}`} exact={true} to={`${match.path}/orders`} /> } */}
          <Route path={`${match.path}/*`} component={ReloadPage} key="spa" />
        </Switch>
      </Layout>
      <DismissLogin />
    </>
  );
};

MainLayout.propTypes = {
  match: PropTypes.object,
  location: PropTypes.object,
};

MainLayout.contextTypes = {
  cookies: PropTypes.object,
};

export default function getRoutes({currentLocale, defaultLocale}) {
  const langPath = getLangPath(currentLocale, defaultLocale);
  const routes = [
    <Route path={`${langPath}/my/orders/lookup`} component={Guest} key="guestorder" />,
    <AuthRoute path={`${langPath}/my`} component={MainLayout} key="myaccount" />,
  ];
  return routes;
}
